import React, { useState } from "react";
import Card from "components/card";
import { MdImage } from "react-icons/md";
import { WeightProgressImage } from "../../types/user";
import BlurredImage from "../common/BlurredImage";
import BlurToggleButton from "../common/BlurToggleButton";
import CardHeader from "../common/CardHeader";
import EmptyState from "../common/EmptyState";
import ImageLightbox, {
  buildFileSlides,
  fileCaptionDate,
} from "../common/ImageLightbox";
import { useImageLightbox } from "../../hooks/useImageLightbox";

interface WeightProgressImagesGalleryProps {
  images: WeightProgressImage[] | null | undefined;
}

const WeightProgressImagesGallery: React.FC<
  WeightProgressImagesGalleryProps
> = ({ images }) => {
  const [isBlurred, setIsBlurred] = useState(true);
  const { openAt, lightboxProps } = useImageLightbox();

  // Same filter buildFileSlides applies, so grid and slide indexes line up.
  const visibleImages = (images || []).filter((image) => !!image.full_path);

  if (visibleImages.length === 0) {
    return (
      <Card extra="w-full p-3 md:p-6">
        <h2 className="mb-4 text-xl font-bold text-navy-700 dark:text-white">
          Weight Progress Images
        </h2>
        <EmptyState
          icon={MdImage}
          title="No weight progress images"
          description="This user has not uploaded any progress photos."
          padding="sm"
        />
      </Card>
    );
  }

  return (
    <Card extra="w-full p-3 md:p-6">
      <CardHeader
        title="Weight Progress Images"
        count={visibleImages.length}
        singular="Image"
        actions={
          <BlurToggleButton
            blurred={isBlurred}
            onToggle={() => setIsBlurred(!isBlurred)}
          />
        }
      />

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6">
        {visibleImages.map((image, index) => (
          <div
            key={image.id}
            className="group relative cursor-pointer overflow-hidden rounded-lg bg-gray-100 transition-transform hover:scale-[101%] dark:bg-navy-800"
            onClick={() => openAt(buildFileSlides(visibleImages), index)}
          >
            {/* Image */}
            <div className="aspect-square w-full overflow-hidden">
              <BlurredImage
                src={image.full_path as string}
                alt={image.original_name}
                blurred={isBlurred}
                className="h-full w-full"
              />
            </div>

            {/* Image Info Overlay */}
            <div className="absolute bottom-2 left-2 flex flex-col justify-end rounded-3xl bg-gray-900 bg-opacity-70 px-3 py-2 transition-all">
              <p className="text-xs text-white">{fileCaptionDate(image)}</p>
            </div>
          </div>
        ))}
      </div>

      <ImageLightbox {...lightboxProps} />
    </Card>
  );
};

export default WeightProgressImagesGallery;
